import { useEffect } from 'react';
import { stallApi } from '@/net/api';
import { invalidateStallSaleSave, syncStallSaleSave } from '@/net/stallSaleSync';
import { useGameStore } from '@/ui/store/gameStore';
import { readSeenSaleIds, salePosition } from '@/ui/stallSales';
import { StallSaleCard } from './StallSaleCard';
import { Icon } from './Icon';

/** HTTP recovers missed/offline sales; WS uses the same ID-deduplicated queue. */
export function StallSaleNotifications(): JSX.Element | null {
  const userId = useGameStore((s) => s.currentUser?.is_guest === false ? s.currentUser.id : null);
  const sale = useGameStore((s) => s.stallSaleNotices[0]);
  const blocked = useGameStore((s) => s.trade !== null || s.openModal !== null || s.hatchPhase !== null
    || s.tradeNotice !== null || s.achievementToasts.length > 0);
  useEffect(() => {
    if (!userId) return;
    let disposed = false;
    let pending = false;
    const key = `capibara:stall-sales:${userId}`;
    let watermark = '';
    // Initial persisted receipts belong to the state loaded at bootstrap. IDs written
    // later by another tab suppress a notice, but must not suppress this tab's save refresh.
    let observed = new Set(readSeenSaleIds(userId));
    try { watermark = localStorage.getItem(key) ?? ''; } catch { /* storage can be disabled */ }
    const refresh = async (): Promise<void> => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const result = await stallApi.sales();
        if (disposed || useGameStore.getState().currentUser?.id !== userId) return;
        const recent = result.records.filter((item) => watermark
          ? salePosition(item) > watermark : Date.parse(item.created_at) > Date.now() - 86_400_000);
        const sessionSeen = useGameStore.getState().seenStallSaleIds;
        const seen = new Set([...readSeenSaleIds(userId), ...sessionSeen]);
        const unseen = recent.filter((item) => !seen.has(item.id));
        const needsRefresh = recent.some((item) => !observed.has(item.id) && !sessionSeen.includes(item.id));
        // A long absence is summarized by the three newest cards; the full list remains in history.
        useGameStore.getState().pushStallSales(unseen.slice(0, 3).reverse());
        if (needsRefresh) invalidateStallSaleSave(userId);
        observed = new Set([...observed, ...result.records.map((item) => item.id)].slice(-500));
        // Already handled WS sales are a no-op; failed save refreshes are retried here.
        void syncStallSaleSave(userId).catch(() => undefined);
        const newest = result.records[0];
        if (newest && salePosition(newest) > watermark) {
          watermark = salePosition(newest);
          try { localStorage.setItem(key, watermark); } catch { /* session deduplication still works */ }
        }
      } catch { /* Recover on focus/next poll. Never interrupt the game with inbox errors. */ }
      finally { pending = false; }
    };
    const onFocus = (): void => { void refresh(); };
    void refresh();
    const timer = setInterval(onFocus, 60_000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { disposed = true; clearInterval(timer); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [userId]);
  useEffect(() => {
    if (!sale || blocked) return;
    const timer = setTimeout(() => useGameStore.getState().dismissStallSale(sale.id), 6_000);
    return () => clearTimeout(timer);
  }, [sale?.id, blocked]);
  if (!userId || !sale || blocked) return null;
  return (
    <aside role="status" aria-live="polite" className="absolute left-1/2 top-16 z-50 w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 rounded-3xl border-2 border-meadow-300 bg-meadow-50 p-2 shadow-xl pointer-events-auto font-game">
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="inline-flex items-center gap-1 text-sm font-extrabold text-meadow-700"><Icon src="/ui/ui_btn_npc_sell.webp" fallback="" alt="" className="h-7 w-7" />Sprzedane!</span>
        <button onClick={() => useGameStore.getState().dismissStallSale(sale.id)} aria-label="Zamknij powiadomienie" className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100"><Icon src="/ui/ui_btn_close.webp" fallback="" alt="" className="h-5 w-5" /></button>
      </div>
      <button onClick={() => { useGameStore.getState().dismissStallSale(sale.id); useGameStore.getState().openTradeHistory(null); }} aria-label="Pokaż sprzedane pety i jajka" className="block w-full text-left">
        <StallSaleCard sale={sale} compact />
      </button>
    </aside>
  );
}
